import { ref, unref, watch, type ComputedRef, type Ref } from 'vue';
import { toast } from 'vue-sonner';
import { isPendingClientTaskWork } from '~/lib/taskClientResponse';
import type { Client, Project, UploadedFile } from '~/types/models';
import type { ClientTaskDraft, ClientDeliveryUploadScopeOption } from '@/components/clients/delivery/types';

type MaybeRef<T> = Ref<T> | ComputedRef<T>;

interface Options {
    client: MaybeRef<Client | null>;
    canMutate: MaybeRef<boolean>;
    relatedProjects: MaybeRef<Project[]>;
    fileScopeEntities: MaybeRef<Array<{ entityType: UploadedFile['entityType']; entityId: string }>>;
    uploadScopeOptions: MaybeRef<ClientDeliveryUploadScopeOption[]>;
    projectStore: any;
    filesStore: any;
    taskStore: any;
    userStore: any;
    onFilesLoaded: (files: UploadedFile[]) => void;
    filesLoading: Ref<boolean>;
}

export function useClientWorkspaceDelivery(options: Options) {
    const isPublishProjectDialogOpen = ref(false);
    const isNoticeProjectDialogOpen = ref(false);
    const publishTargetProject = ref<Project | null>(null);
    const noticeTargetProject = ref<Project | null>(null);
    const selectedTaskId = ref<string | null>(null);
    const isTaskDetailOpen = ref(false);
    const selectedDeliveryFiles = ref<File[]>([]);
    const deliveryUploadScope = ref('');
    const deliveryUploading = ref(false);
    const deliveryUploadError = ref('');

    watch(options.uploadScopeOptions, (scopeOptions) => {
        if (!scopeOptions.length) {
            deliveryUploadScope.value = '';
            return;
        }
        if (!scopeOptions.some((option) => option.value === deliveryUploadScope.value)) {
            deliveryUploadScope.value = scopeOptions[0]?.value || '';
        }
    }, { immediate: true });

    function openPublishProjectDialog(project: Project) {
        publishTargetProject.value = project;
        isPublishProjectDialogOpen.value = true;
    }

    function openProjectNoticeDialog(project: Project) {
        noticeTargetProject.value = project;
        isNoticeProjectDialogOpen.value = true;
    }

    async function archiveRelatedProject(project: Project) {
        await options.projectStore.updateProject(project.id, { status: 'Archived' });
    }

    async function deleteRelatedProject(project: Project) {
        await options.projectStore.deleteProject(project.id);
    }

    function handleDeliveryFilesSelected(files: File[]) {
        selectedDeliveryFiles.value = files.filter((file) => file.size > 0);
    }

    async function refreshScopedFiles() {
        if (!unref(options.client)?.id) return;
        options.filesLoading.value = true;
        deliveryUploadError.value = '';
        try {
            const files = await options.filesStore.fetchFilesForScope(unref(options.fileScopeEntities));
            options.onFilesLoaded(files);
            return files;
        } catch (error: any) {
            deliveryUploadError.value = error?.message || 'Could not refresh files.';
            return [];
        } finally {
            options.filesLoading.value = false;
        }
    }

    async function uploadDeliveryFiles() {
        if (!unref(options.canMutate) || !selectedDeliveryFiles.value.length || !deliveryUploadScope.value) return;
        const [entityType, entityId] = deliveryUploadScope.value.split(':');
        if (!entityType || !entityId) return;

        deliveryUploading.value = true;
        deliveryUploadError.value = '';
        try {
            for (const file of selectedDeliveryFiles.value) {
                if (entityType === 'project' && file.type.startsWith('image/')) {
                    await options.filesStore.uploadGalleryImage(entityId, file, { visibleInPortal: true });
                } else {
                    await options.filesStore.uploadFile(entityType as UploadedFile['entityType'], entityId, file);
                }
            }
            selectedDeliveryFiles.value = [];
            await refreshScopedFiles();
            toast.success('Delivery files uploaded.');
        } catch (error: any) {
            deliveryUploadError.value = error?.message || 'Could not upload files.';
            toast.error(deliveryUploadError.value);
        } finally {
            deliveryUploading.value = false;
        }
    }

    function openTaskDetail(taskId: string) {
        selectedTaskId.value = taskId;
        isTaskDetailOpen.value = true;
    }

    async function handleTaskUpdated() {
        await options.taskStore.fetchTasks();
    }

    function handleTaskDeleted() {
        isTaskDetailOpen.value = false;
        selectedTaskId.value = null;
    }

    async function handleTaskCreated(taskDraft: ClientTaskDraft) {
        const client = unref(options.client);
        if (!client?.id) return false;
        try {
            await options.taskStore.addTask({
                title: taskDraft.title.trim(),
                description: taskDraft.description.trim() || undefined,
                status: 'Todo',
                priority: taskDraft.priority || undefined,
                dueDate: taskDraft.dueDate || undefined,
                projectId: taskDraft.projectId || undefined,
                milestoneId: taskDraft.projectId && taskDraft.milestoneId ? taskDraft.milestoneId : undefined,
                clientId: client.id,
                client: client.name || undefined,
                clientVisible: taskDraft.clientVisible,
                clientResponseRequired: taskDraft.clientVisible ? taskDraft.clientResponseRequired : false,
                clientResponseStatus: taskDraft.clientVisible && taskDraft.clientResponseRequired ? 'waiting' : undefined,
            }, options.userStore.userId || 'unknown');
            await options.taskStore.fetchTasks();
            toast.success('Task created.');
            return true;
        } catch (error: any) {
            toast.error(error?.message || 'Could not create task.');
            return false;
        }
    }

    return {
        isPublishProjectDialogOpen,
        isNoticeProjectDialogOpen,
        publishTargetProject,
        noticeTargetProject,
        selectedTaskId,
        isTaskDetailOpen,
        selectedDeliveryFiles,
        deliveryUploadScope,
        deliveryUploading,
        deliveryUploadError,
        openPublishProjectDialog,
        openProjectNoticeDialog,
        archiveRelatedProject,
        deleteRelatedProject,
        handleDeliveryFilesSelected,
        refreshScopedFiles,
        uploadDeliveryFiles,
        openTaskDetail,
        handleTaskUpdated,
        handleTaskDeleted,
        handleTaskCreated,
    };
}
